// Shared image-insert path. Used by the toolbar Image button, clipboard paste,
// and drag-and-drop in PlateEditor.tsx. Performs MIME magic-byte validation
// (D1), generates a placeholder id, inserts the image with the base64 URL
// + transient `_uploading` flag, then asynchronously uploads to S3 (D2).
// When the upload returns a docId, the node is updated in place via setNodes.

import { collectRemoteImageNodes, registerPendingUpload, uploadSingleImage } from '../../services/imageUploadService';
import { fetchImageViaProxy } from '../../services/imageProxyClient';
import { sniffImageMime } from './imageMimeSniff';

const MAX_BYTES = 8 * 1024 * 1024;
const UPLOAD_DEBOUNCE_MS = 500;
// Default upload source tag attached to images inserted from the one-pager editor.
const DEFAULT_IMAGE_SOURCE = 'one-pager';

type InsertError = 'invalid-mime' | 'too-large' | 'read-failed';

export interface InsertResult {
    error?: InsertError;
    placeholderId?: string;
}

// FileReader.readAsDataURL works on any Blob (a File is a Blob), so the same
// reader serves both clipboard image files and fetched remote-image blobs.
function readAsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

function genPlaceholderId(): string {
    return `up_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function findPathByPlaceholder(editor: any, placeholderId: string): number[] | null {
    const root = editor?.children;
    if (!Array.isArray(root)) return null;
    const walk = (nodes: any[], parentPath: number[]): number[] | null => {
        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (!n || typeof n !== 'object') continue;
            const path = [...parentPath, i];
            if (n.type === 'image' && n._uploading === placeholderId) return path;
            if (Array.isArray(n.children)) {
                const found = walk(n.children, path);
                if (found) return found;
            }
        }
        return null;
    };
    return walk(root, []);
}

async function startLazyUpload(editor: any, placeholderId: string, dataUrl: string, source: string): Promise<void> {
    // Debounce so a burst of inserts (e.g. paste of HTML with multiple imgs)
    // doesn't fire upload-all at once before the editor settles.
    await new Promise((r) => setTimeout(r, UPLOAD_DEBOUNCE_MS));
    try {
        const docId = await uploadSingleImage(dataUrl, source);
        const path = findPathByPlaceholder(editor, placeholderId);
        if (!path) return;
        try {
            editor?.tf?.setNodes?.({ url: docId, _uploading: undefined, _uploadError: undefined }, { at: path });
        } catch {
            /* defensive: editor may have unmounted */
        }
    } catch (err) {
        console.error('[handleImageInsert] upload failed', err);
        const path = findPathByPlaceholder(editor, placeholderId);
        if (!path) return;
        try {
            editor?.tf?.setNodes?.({ _uploading: undefined, _uploadError: true }, { at: path });
        } catch {
            /* defensive */
        }
    }
}

export async function handleImageInsert(file: File, editor: any, source = DEFAULT_IMAGE_SOURCE): Promise<InsertResult> {
    if (!file) return { error: 'invalid-mime' };
    if (file.size > MAX_BYTES) return { error: 'too-large' };

    const sniffedMime = await sniffImageMime(file);
    if (!sniffedMime) return { error: 'invalid-mime' };

    let dataUrl: string;
    try {
        dataUrl = await readAsDataUrl(file);
    } catch (err) {
        console.error('[handleImageInsert] failed to read file', err);
        return { error: 'read-failed' };
    }

    const placeholderId = genPlaceholderId();
    try {
        editor?.tf?.insertNodes?.({
            type: 'image',
            url: dataUrl,
            _uploading: placeholderId,
            children: [{ text: '' }],
        });
    } catch (err) {
        console.error('[handleImageInsert] insertNodes failed', err);
        return { error: 'read-failed' };
    }

    const uploadPromise = startLazyUpload(editor, placeholderId, dataUrl, source);
    registerPendingUpload(placeholderId, uploadPromise);

    return { placeholderId };
}

// Fetch a remote image → base64 → upload to S3 → swap the node's url to the
// returned docId. Mirrors startLazyUpload but with a network fetch as the first
// step. The direct client fetch is tried first; if it throws (CORS-blocked
// host, e.g. *.googleusercontent.com, or network error) or returns a non-OK
// status, we fall back to the server-side image proxy Lambda, which fetches the
// bytes without the browser's CORS restriction and returns a base64 data: URL.
// On any failure of BOTH paths the remote URL is LEFT IN PLACE and we warn —
// graceful degradation, never throw. The `_uploading` placeholder is cleared
// either way so the upload chip hides.
async function rehostSingleRemoteImage(editor: any, placeholderId: string, url: string, source: string): Promise<void> {
    let dataUrl: string | null = null;
    try {
        // credentials:'omit' keeps the request anonymous; some user-content
        // hosts reject credentialed cross-origin requests outright.
        const res = await fetch(url, { credentials: 'omit' });
        if (res.ok) {
            dataUrl = await readAsDataUrl(await res.blob());
        } else {
            console.warn('[paste] remote image direct fetch non-ok, trying proxy', { url, status: res.status });
            dataUrl = await fetchImageViaProxy(url);
        }
    } catch (err) {
        // CORS-blocked fetches reject here (no status). Try the server-side proxy.
        console.warn('[paste] remote image direct fetch failed, trying proxy', { url, message: (err as any)?.message });
        dataUrl = await fetchImageViaProxy(url);
    }

    if (!dataUrl) {
        // Both the direct fetch and the proxy failed (or no proxy configured).
        // Keep the remote URL so the image still renders until its source expires.
        clearRemotePlaceholder(editor, placeholderId);
        return;
    }

    try {
        const docId = await uploadSingleImage(dataUrl, source);
        const path = findPathByPlaceholder(editor, placeholderId);
        if (!path) return;
        try {
            editor?.tf?.setNodes?.({ url: docId, _uploading: undefined, _uploadError: undefined }, { at: path });
        } catch {
            /* defensive: editor may have unmounted */
        }
    } catch (err) {
        console.warn('[paste] remote image persist failed', { url, status: (err as any)?.message });
        clearRemotePlaceholder(editor, placeholderId);
    }
}

function clearRemotePlaceholder(editor: any, placeholderId: string): void {
    const path = findPathByPlaceholder(editor, placeholderId);
    if (!path) return;
    try {
        editor?.tf?.setNodes?.({ _uploading: undefined }, { at: path });
    } catch {
        /* defensive */
    }
}

/**
 * After a paste inserts a fragment, find every freshly-inserted image whose
 * url is a remote http(s) URL, tag it with an `_uploading` placeholder, and
 * kick off a fetch → base64 → S3 upload for each so the image survives past
 * the source URL's expiry. Each task is registered as a pending upload so save
 * waits for it. Returns the number of rehost tasks started.
 *
 * Call AFTER insertFragment so the nodes already live in editor.children.
 */
export function rehostRemoteImagesAfterPaste(editor: any, source = DEFAULT_IMAGE_SOURCE): number {
    const remote = collectRemoteImageNodes(editor?.children ?? []);
    if (!remote.length) return 0;
    for (const { path } of remote) {
        const placeholderId = genPlaceholderId();
        try {
            editor?.tf?.setNodes?.({ _uploading: placeholderId }, { at: path });
        } catch {
            continue;
        }
        // Re-read the url from the tagged node rather than trusting the pre-tag
        // value — defensive against any normalization between collect and tag.
        const uploadPromise = rehostSingleRemoteImage(editor, placeholderId, remoteUrlAtPath(editor, path) ?? '', source);
        registerPendingUpload(placeholderId, uploadPromise);
    }
    return remote.length;
}

function remoteUrlAtPath(editor: any, path: number[]): string | null {
    let node: any = editor?.children;
    for (let i = 0; i < path.length; i++) {
        if (!Array.isArray(node)) return null;
        const next = node[path[i]];
        node = i === path.length - 1 ? next : next?.children;
    }
    return typeof node?.url === 'string' ? node.url : null;
}
